// Vertical card-stacking deck for image parallax section

// Header sticky state constants
const HEADER_STICKY_DECK = 'deck';
const HEADER_OWNED_FLAG = 'deck-owns-header';

// Sticky header class names
const HEADER_CLASSES = {
  stuck: 'js__header__stuck',
  animated: 'js__header__stuck--animated',
  shown: 'js__header__stuck--trigger-animation',
  backdrop: 'js__header__stuck__backdrop'
};

class ImageParallax extends HTMLElement {
  constructor() {
    super();
    this.deck = null;
  }

  connectedCallback() {
    this._initDeck();
    this._initAccordions();
    this._initBackButton();
  }

  _initBackButton() {
    const backBtn = this.querySelector('[data-back-button]');
    if (!backBtn) return;
    this._onBackClick = (e) => {
      if (document.referrer && document.referrer.indexOf(window.location.host) !== -1) {
        e.preventDefault();
        window.history.back();
      }
    };
    backBtn.addEventListener('click', this._onBackClick);
  }

  _initDeck() {
    const deckEl = this.querySelector('[data-deck]');
    if (!deckEl) return;

    // Fallback to native stacked layout if snap is disabled
    if (this.dataset.enableSnap === 'false') {
      deckEl.classList.add('sip-deck--native');
      return;
    }

    const start = () => {
      if (typeof window.CardDeck === 'undefined') {
        // Wait for deferred card-deck script
        requestAnimationFrame(start);
        return;
      }
      this.deck = new window.CardDeck(deckEl);
      this._markScrollableSlides();
      this._initVideoGating();
      this._initHeaderOwnership();
      this._initAutoplay();
      this._initBackgroundPreloader();
    };

    start();
  }

  // Idle prefetch for upcoming slide images
  _initBackgroundPreloader() {
    const warmNextImages = () => {
      if (!this.deck || this.deck.destroyed) return;
      const lazyImgs = Array.prototype.slice.call(this.querySelectorAll('.sip-deck img[loading="lazy"]'));
      if (!lazyImgs.length) return;

      let idx = 0;
      const loadNext = () => {
        if (idx >= lazyImgs.length) return;
        const img = lazyImgs[idx++];
        if (img.dataset.preloaded) {
          loadNext();
          return;
        }
        img.dataset.preloaded = 'true';
        const dummy = new Image();
        dummy.decoding = 'async';
        if (img.srcset) dummy.srcset = img.srcset;
        if (img.sizes) dummy.sizes = img.sizes;
        dummy.src = img.src || img.currentSrc;
        dummy.onload = dummy.onerror = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadNext, { timeout: 800 });
          } else {
            setTimeout(loadNext, 120);
          }
        };
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadNext, { timeout: 1200 });
      } else {
        setTimeout(loadNext, 300);
      }
    };

    const firstImg = this.querySelector('.sip-deck img[loading="eager"]') || this.querySelector('.sip-deck img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', () => setTimeout(warmNextImages, 800), { once: true });
    } else {
      setTimeout(warmNextImages, 1200);
    }
  }

  // Slide autoplay progression
  _initAutoplay() {
    if (!this.deck) return;
    if (this.dataset.enableAutoplay !== 'true') return;

    const intervalSec = parseFloat(this.dataset.autoplayInterval) || 5;
    this._autoplayDelay = Math.max(1, Math.min(10, intervalSec)) * 1000;
    this._autoplayTimer = null;
    this._autoplayIntersecting = false;

    this._startAutoplay = () => {
      this._stopAutoplay();
      if (!this._autoplayIntersecting) return;
      this._autoplayTimer = setInterval(() => {
        if (!this.deck || this.deck.destroyed || this.deck.locked) return;
        if (this.deck.animating || (this.deck._drag && this.deck._drag.decided)) return;
        if (!this._autoplayIntersecting) return;

        if (this.deck.isEnd()) {
          this.deck.goTo(0);
        } else {
          this.deck.next();
        }
      }, this._autoplayDelay);
    };

    this._stopAutoplay = () => {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    };

    this._resetAutoplay = () => {
      if (this.dataset.enableAutoplay !== 'true') return;
      this._startAutoplay();
    };

    // Reset autoplay on user activity
    this._onDeckUserActivity = () => {
      this._resetAutoplay();
    };

    this.deck.root.addEventListener('deck:change', this._onDeckUserActivity);
    this.deck.root.addEventListener('deck:dragstart', this._onDeckUserActivity);
    this.addEventListener('touchstart', this._onDeckUserActivity, { passive: true });
    this.addEventListener('wheel', this._onDeckUserActivity, { passive: true });

    // Pause autoplay when off screen
    if ('IntersectionObserver' in window) {
      this._autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this._autoplayIntersecting = entry.isIntersecting && entry.intersectionRatio >= 0.4;
          if (this._autoplayIntersecting) {
            this._startAutoplay();
          } else {
            this._stopAutoplay();
          }
        });
      }, { threshold: [0, 0.4, 1.0] });
      this._autoplayObserver.observe(this);
    } else {
      this._autoplayIntersecting = true;
      this._startAutoplay();
    }
  }

  // Play videos only for active and adjacent slides to conserve resources
  _initVideoGating() {
    if (!this.deck) return;

    const videos = this.deck.slides.map((slide) => slide.querySelectorAll('video'));
    if (!videos.some((list) => list.length)) return;

    const apply = () => {
      const active = this.deck.index;
      videos.forEach((list, i) => {
        // Play active and adjacent slides
        const shouldPlay = Math.abs(i - active) <= 1;
        list.forEach((video) => {
          if (shouldPlay) {
            if (video.paused) {
              // Silence unhandled autoplay rejections
              const p = video.play();
              if (p && p.catch) p.catch(() => {});
            }
          } else if (!video.paused) {
            // Pause distant video and clear autoplay
            video.autoplay = false;
            video.pause();
          } else {
            video.autoplay = false;
          }
        });
      });
    };

    this._onDeckVideoSync = apply;
    this._videoDeckEl = this.deck.root;
    // Sync video on drag and settle
    this._videoDeckEl.addEventListener('deck:change', apply);
    this._videoDeckEl.addEventListener('deck:dragstart', apply);
    apply();
  }

  // Mark slides as scrollable when content overflows
  _markScrollableSlides() {
    if (!this.deck) return;
    this.deck.slides.forEach((slide) => {
      if (!slide.hasAttribute('data-deck-scrollable')) return;
      const overflows = slide.scrollHeight > slide.clientHeight + 4;
      slide.classList.toggle('deck__slide--scrollable', overflows);
    });
  }

  _initAccordions() {
    this._onAccordionClick = (e) => {
      if (window.innerWidth >= 768) return;
      const trigger = e.target.closest('[data-sip-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const col = trigger.closest('.sip__footer-col');
      if (!col) return;
      const body = col.querySelector('[data-sip-accordion-body]');
      if (!body) return;

      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isExpanded);
      trigger.classList.toggle('is-open', !isExpanded);
      body.classList.toggle('is-open', !isExpanded);

      // Recalculate slide scrollability on accordion toggle
      if (this.deck) {
        this.deck.update();
        setTimeout(() => this._markScrollableSlides(), 380);
      }
    };
    this.addEventListener('click', this._onAccordionClick);

    this._onResize = () => this._markScrollableSlides();
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  // Manage sticky header visibility with card deck state
  _initHeaderOwnership() {
    if (!this.deck) return;
    if (!this._ownsHeader()) return;

    this._headerVisible = null;
    this._headerVisibleBeforeDrag = null;

    if (!this._claimHeader(document.querySelector('[data-header-wrapper]'))) return;

    const stateFor = (index, previousIndex) => {
      // Header stays visible on first slide
      if (index === 0) return true;
      return index < previousIndex;
    };

    this._onDeckHeaderChange = (e) => {
      this._setHeaderVisible(stateFor(e.detail.index, e.detail.previousIndex));
    };

    // Predict header visibility during drag
    this._onDeckHeaderDragStart = (e) => {
      this._headerVisibleBeforeDrag = this._headerVisible;
      this._setHeaderVisible(stateFor(e.detail.targetIndex, e.detail.index));
    };

    // Revert header prediction if drag canceled
    this._onDeckHeaderDragEnd = (e) => {
      if (e.detail.committed) return;
      this._setHeaderVisible(this._headerVisibleBeforeDrag);
    };

    this.deck.root.addEventListener('deck:change', this._onDeckHeaderChange);
    this.deck.root.addEventListener('deck:dragstart', this._onDeckHeaderDragStart);
    this.deck.root.addEventListener('deck:dragend', this._onDeckHeaderDragEnd);

    // Re-claim header on section reload
    this._onHeaderSectionLoad = (e) => {
      const el = e.target;
      if (!el || typeof el.matches !== 'function') return;
      if (!el.matches('[data-header-wrapper]')) return;
      this._headerVisible = null;
      this._claimHeader(el);
    };
    document.addEventListener('shopify:section:load', this._onHeaderSectionLoad);
  }

  // Deck only controls header when section is first child
  _ownsHeader() {
    const section = this.closest('.shopify-section');
    return !!section && section.matches('.main-content > .shopify-section:first-child');
  }

  // Attach deck control to header wrapper
  _claimHeader(wrapper) {
    if (!wrapper) return false;
    this._headerWrapper = wrapper;

    // Preserve original header sticky attribute
    if (wrapper.dataset.headerSticky !== HEADER_STICKY_DECK) {
      wrapper.dataset.headerStickyBase = wrapper.dataset.headerSticky || '';
    }
    wrapper.dataset.headerSticky = HEADER_STICKY_DECK;
    document.documentElement.classList.add(HEADER_OWNED_FLAG);

    // Apply fixed overlay classes to header
    wrapper.classList.add(HEADER_CLASSES.stuck, HEADER_CLASSES.animated);
    wrapper.classList.remove(HEADER_CLASSES.backdrop);

    this._setHeaderVisible(this.deck ? this.deck.index === 0 : true);
    return true;
  }

  // Toggle header visibility class
  _setHeaderVisible(visible) {
    if (!this._headerWrapper) return;
    visible = !!visible;
    if (this._headerVisible === visible) return;
    this._headerVisible = visible;
    this._headerWrapper.classList.toggle(HEADER_CLASSES.shown, visible);
  }

  // Release header ownership
  _releaseHeader() {
    if (this._onHeaderSectionLoad) {
      document.removeEventListener('shopify:section:load', this._onHeaderSectionLoad);
      this._onHeaderSectionLoad = null;
    }
    if (this.deck && this._onDeckHeaderChange) {
      this.deck.root.removeEventListener('deck:change', this._onDeckHeaderChange);
      this.deck.root.removeEventListener('deck:dragstart', this._onDeckHeaderDragStart);
      this.deck.root.removeEventListener('deck:dragend', this._onDeckHeaderDragEnd);
    }
    this._onDeckHeaderChange = null;
    this._onDeckHeaderDragStart = null;
    this._onDeckHeaderDragEnd = null;

    const wrapper = this._headerWrapper;
    this._headerWrapper = null;
    this._headerVisible = null;
    if (!wrapper) return;

    wrapper.classList.remove(HEADER_CLASSES.stuck, HEADER_CLASSES.animated, HEADER_CLASSES.shown);
    if (typeof wrapper.dataset.headerStickyBase === 'string') {
      wrapper.dataset.headerSticky = wrapper.dataset.headerStickyBase;
      delete wrapper.dataset.headerStickyBase;
    }
    // Remove flag if no other deck holds header
    if (!document.querySelector('image-parallax')) {
      document.documentElement.classList.remove(HEADER_OWNED_FLAG);
    }
  }

  disconnectedCallback() {
    if (this._onAccordionClick) {
      this.removeEventListener('click', this._onAccordionClick);
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
    // Remove video event listeners
    if (this._videoDeckEl && this._onDeckVideoSync) {
      this._videoDeckEl.removeEventListener('deck:change', this._onDeckVideoSync);
      this._videoDeckEl.removeEventListener('deck:dragstart', this._onDeckVideoSync);
      this._videoDeckEl = null;
      this._onDeckVideoSync = null;
    }
    // Autoplay cleanup
    if (this._stopAutoplay) {
      this._stopAutoplay();
    }
    if (this._autoplayObserver) {
      this._autoplayObserver.disconnect();
      this._autoplayObserver = null;
    }
    if (this.deck && this._onDeckUserActivity) {
      this.deck.root.removeEventListener('deck:change', this._onDeckUserActivity);
      this.deck.root.removeEventListener('deck:dragstart', this._onDeckUserActivity);
      this.removeEventListener('touchstart', this._onDeckUserActivity);
      this.removeEventListener('wheel', this._onDeckUserActivity);
      this._onDeckUserActivity = null;
    }

    // Release header before destroying deck
    this._releaseHeader();
    if (this.deck) {
      this.deck.destroy();
      this.deck = null;
    }
  }
}

if (!customElements.get('image-parallax')) {
  customElements.define('image-parallax', ImageParallax);
}
